import type { BillDTO, BillPaymentDTO, BillStatus, PaymentKind } from '@six-pockets/shared';
import type { Tone } from '@/components/ui';
import { fromDateKey, toDateKey } from '@/utils/date';

export const BILL_STATUS: Record<BillStatus, { label: string; tone: Tone }> = {
  settled: { label: 'Settled', tone: 'green' },
  partial: { label: 'Partial', tone: 'amber' },
  balance: { label: 'Balance', tone: 'red' },
  waived: { label: 'Waived', tone: 'grey' },
};

/** "today", "yesterday", "5 days ago": how long a bill has been waiting (by calendar day). */
export function billAge(iso: string): string {
  const days = Math.round((fromDateKey(toDateKey(new Date())).getTime() - fromDateKey(toDateKey(new Date(iso))).getTime()) / 86_400_000);
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}

/** The names on a bill's bookings and tabs, for a bill billed to no one: "Rahul, Amit". */
export function namesOnBill(bill: BillDTO): string {
  const names = [...bill.bookings.map((b) => b.customerName), ...bill.condimentSales.map((s) => s.tabName)];
  return [...new Set(names.map((n) => n.trim()).filter(Boolean))].join(', ');
}

/** Who a bill is for: the customer, else the names on it, else "Walk-in". */
export function billParty(bill: BillDTO): string {
  return bill.customer?.name ?? (namesOnBill(bill) || 'Walk-in');
}

/** "Table 1 + Pool Table · 2 tabs", or "1 tab" for a condiments-only bill. */
export function billContents(bill: BillDTO): string {
  const tables = [...new Set(bill.bookings.map((b) => b.tableName))].join(' + ');
  const n = bill.condimentSales.length;
  const tabs = n ? `${n} ${n === 1 ? 'tab' : 'tabs'}` : '';
  return [tables, tabs].filter(Boolean).join(' · ');
}

/** What a payment was made with: one part, or UPI then cash for a split. */
export function paymentParts(p: Pick<BillPaymentDTO, 'method' | 'amount' | 'upi' | 'cash'>): { label: string; amount: number }[] {
  if (p.method === 'upi') return [{ label: 'UPI', amount: p.amount }];
  if (p.method === 'cash') return [{ label: 'Cash', amount: p.amount }];
  return [
    { label: 'UPI', amount: p.upi },
    { label: 'Cash', amount: p.cash },
  ];
}

const sameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

/**
 * How a payment came in. A Pay now counts as a balance recovered only on a bill from an earlier day (as Reports and
 * Recovered count it); paid later on the bill's own day, it was just paid after billing. `billCreatedAt` is the
 * oldest bill it went to.
 */
export function paymentKindLabel(kind: PaymentKind, paidAt: string, billCreatedAt: string): string {
  if (kind === 'billing') return 'Paid at billing';
  return sameDay(paidAt, billCreatedAt) ? 'Paid after billing' : 'Balance recovered';
}
